<template>
  <div class="row">
    <div class="col-sm-12 col-md-6 p-0">
      <v-calendar
        class="compact-calendar"
        ref="events-calendar"
        color="red"
        :min-date="minimumDate"
        :attributes="calendarOptions"
        @update:from-page="calendarPageNavigated"
        @dayclick="calendarDateClicked"
        :locale="getNavigatorLanguage"
      >
        <template v-slot:header-left-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-left"></i>
          </div>
        </template>
        <template v-slot:header-right-button>
          <div class="calendar-navigation-btn">
            <i class="far fa-long-arrow-right"></i>
          </div>
        </template>
      </v-calendar>
    </div>
    <div class="col-sm-12 col-md-6 p-0">
      <CompactEventsList
        :events="selectedDailyEvents"
        :maxEvents="MAX_DISPLAY_EVENTS"
        :openInNewWindow="openInNewWindow"
        :loading="isLoading"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import CompactEventsList from '@/web/components/events/CompactEventsList';
import colors from '@/core/styles/colors.scss';
import { getNavigatorLanguage } from '@/web/utils/languages';

export default Vue.extend({
  name: 'CompactEventsCalendar',
  components: {
    CompactEventsList,
  },
  props: {
    studioURL: {
      type: String,
    },
    openInNewWindow: {
      type: Boolean,
      default: false,
    },
    isRegistered: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
      studioEvents: 'studio/studioEvents',
      upcomingStudioEvents: 'studio/upcomingStudioEvents',
    }),
    calendarOptions() {
      return [...this.calendarOptionsConfig, this.dottedDays];
    },
    selectedDailyEvents() {
      if (this.isLoading) return [];
      const selectedDateEvents = this.upcomingStudioEvents.filter(event =>
        moment(event.event_start_datetime).isSame(this.selectedDate, 'day')
      );
      if (selectedDateEvents.length && this.daySelected) {
        return selectedDateEvents;
      } else {
        return this.upcomingStudioEvents;
      }
    },
    getNavigatorLanguage,
  },
  data() {
    return {
      MAX_DISPLAY_EVENTS: 5,
      selectedCalendarPage: {
        month: new Date().getMonth(),
        year: new Date().getFullYear(),
      },
      selectedDate: new Date(),
      minimumDate: new Date(),
      dottedDays: {},
      calendarOptionsConfig: [
        {
          key: 'today',
          highlight: {
            class: 'todays-date-background',
            contentClass: 'todays-date-content',
          },
          dates: new Date(),
        },
      ],
      daySelected: false,
      isLoading: true,
    };
  },
  methods: {
    calendarPageNavigated(page) {
      this.selectedCalendarPage = page;
      this.getStudioEvents();
    },
    calendarDateClicked(day) {
      this.selectedDate = moment(day.date).subtract(12, 'hours').toDate();
      this.daySelected = true;
      this.getUpcomingStudioEvents();
    },
    async getStudioEvents() {
      const y = this.selectedCalendarPage.year,
        m = this.selectedCalendarPage.month - 1;
      const firstDay = moment(new Date(y, m, 1));
      const lastDay = moment(new Date(y, m + 1, 0)).add('days', 1);
      await this.$store.dispatch('studio/getStudioEventsWithFilter', {
        studioURL: this.studioURL,
        fromDate: firstDay,
        toDate: lastDay,
        isRegistered: this.isRegistered,
      });
      if (!this.studioEvents) return;
      const dottedDays = [];
      this.studioEvents.forEach(event => {
        dottedDays.push(moment(event.event_start_datetime).toDate());
        // find oldest event and store it in minimumDate
        if (moment(event.event_start_datetime).isBefore(this.minimumDate)) {
          this.minimumDate = moment(event.event_start_datetime).toDate();
        }
      });
      this.dottedDays = {
        dot: {
          style: {
            backgroundColor: this.studio?.primary_color || colors.coral,
          },
        },
        dates: dottedDays,
      };
      // move to first date with events
      // commented out, as it causes the whole page to jump down to calendar
      // setTimeout(() => {
      //   if (dottedDays.length > 0) {
      //     this.$refs['events-calendar'].focusDate(dottedDays[0]);
      //     this.selectedDate = dottedDays[0];
      //   }
      // }, 100)
    },
    async getUpcomingStudioEvents() {
      this.isLoading = true;
      try {
        await this.$store.dispatch('studio/getUpcomingStudioEvents', {
          studioURL: this.studioURL,
          fromDate: this.selectedDate,
          isRegistered: this.isRegistered,
        });
        this.upcomingStudioEvents.forEach(event => {
          if (moment(event.event_start_datetime).isBefore(this.minimumDate)) {
            this.minimumDate = moment(event.event_start_datetime).toDate();
          }
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.getUpcomingStudioEvents();
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.compact-calendar {
  font-family: var(--brand-font, 'Poppins');
}

::v-deep .todays-date-background {
  background-color: $charcoal;
  border-radius: 50%;
  box-sizing: content-box;
}

::v-deep .todays-date-content {
  color: $white;
}

::v-deep .vc-day-content {
  padding: 1rem;
}

/* move dot a bit lower*/
::v-deep .selectedDailyEvents {
  height: 38px;
}

/* fix circle on small devices */
::v-deep .vc-day {
  min-width: 28px;
}

::v-deep .vc-dots {
  height: 0;
}

/* move buttons away from borders */
::v-deep .vc-arrows-container {
  padding-top: 12px;
  padding-left: 36px;
  padding-right: 36px;

  @media screen and (max-width: 992px) {
    padding-left: 20px;
    padding-right: 20px;
  }
}

::v-deep .vc-arrow {
  &:hover {
    background-color: white;
  }
}

::v-deep .vc-day-box-center-bottom {
  height: 40px;
}

::v-deep .vc-container {
  color: $dark-gray;
  width: 100%;
  max-width: 100%;
  border: none;
}

::v-deep .vc-day-content {
  &:hover {
    background-color: $dark-gray !important;
    color: $white !important;
  }
  &:focus {
    background-color: var(--brand-color, $coral) !important;
    color: $white !important;
  }
}

::v-deep .vc-header {
  margin-bottom: 1rem;
}

::v-deep .vc-grid-cell {
  padding: 0.5rem;
}

::v-deep .vc-title {
  color: $dark-gray !important;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 6px 10px !important;
  font-family: var(--brand-font, 'Poppins');

  &:before {
    color: var(--brand-color, $coral);
    content: '';
    font-family: 'Font Awesome 5 Pro';
    font-weight: 400;
    display: inline-block;
    margin-right: 5px;
  }
}

::v-deep .vc-nav-popover-container {
  background-color: $charcoal;
}

.calendar-navigation-btn {
  margin-top: 8px;
  border-radius: 6px;
  border: 1px solid $medium-gray;
  padding: 6px 10px;

  &:hover {
    color: var(--brand-color, $coral);
    border-color: var(--brand-color, $coral);
    box-shadow: 0px 0px 1px 1px var(--brand-color, $coral);
    background-color: $white;
  }
}
</style>
